import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import UISpinner from 'Components/UIComponents/UISpinner';
import { useGetInvoiceDownloadLink } from 'Hooks/Billing';
import { useAttachmentDownload } from 'Hooks/Common';
import { Invoice, InvoiceDownloadLink } from 'Interfaces/Billing';
import { AuthorizedRoutePaths, settingsRedirectionMap } from 'Interfaces/RoutePaths';
import Toast from 'Services/Toast';
import { isNotEmpty } from 'Utils/functions';

type PageParams = {
  invoiceId: Invoice['id'];
};

export const InvoiceDownload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<PageParams>();

  const [getInvoiceDownloadLink] = useGetInvoiceDownloadLink();
  const [downloadInvoice, isReady] = useAttachmentDownload();
  const handleDownloadInvoice = useCallback(
    async (payload: InvoiceDownloadLink) => {
      try {
        const result = await getInvoiceDownloadLink(payload);
        if (isNotEmpty(result)) {
          await downloadInvoice(result.result);
        }
        Toast.success('File is downloading');
        navigate(settingsRedirectionMap[AuthorizedRoutePaths.SETTINGS_BILLING]);
      } catch (error) {
        console.error(error);
        Toast.handleErrors(error);
      }
    },
    [downloadInvoice, getInvoiceDownloadLink, navigate],
  );

  const invoiceId = params.invoiceId;
  const searchString = location.search;

  const hash = useMemo(() => {
    const urlParams = new URLSearchParams(searchString.replace('?', ''));
    return urlParams.get('hash');
  }, [searchString]);

  useEffect(() => {
    if (invoiceId && hash && isReady) {
      handleDownloadInvoice({ invoiceId, hash });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, hash, isReady]);

  return <UISpinner width={50} height={50} wrapperClassName="spinner--main__wrapper" />;
};
